import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Play, ChevronLeft, ChevronRight, Film } from 'lucide-react';
import NetworkVideoModal from './NetworkVideoModal';
import { resolveImageUrl } from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FlytFilmsCarousel — horizontal slider on the FLYT homepage. Shows
 * ONLY network case-study videos pulled from /api/flyt/examples. Every
 * card opens the NetworkVideoModal in-place (no page nav) so viewers
 * see the film + the case-study breakdown side by side.
 */
const FlytFilmsCarousel = () => {
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilm, setActiveFilm] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/flyt/examples`)
      .then((r) => {
        // Defensive: only keep entries with a playable video so the slate
        // never shows an empty card or a dead-end click.
        const list = (r.data || []).filter((ex) => ex && ex.video_url);
        setFilms(list);
      })
      .catch(() => setFilms([]))
      .finally(() => setLoading(false));
  }, []);

  const scrollBy = (dir) => {
    if (!trackRef.current) return;
    const w = trackRef.current.clientWidth;
    trackRef.current.scrollBy({ left: dir * (w * 0.8), behavior: 'smooth' });
  };

  // Compose a short case-study body from the FlytExample fields so the
  // modal panel reads like an Opendorse/Network case-study breakdown.
  const buildBody = (ex) => {
    if (!ex) return '';
    const blocks = [];
    if (ex.ideation) blocks.push(`Ideation\n${ex.ideation}`);
    if (ex.goal) blocks.push(`Goal\n${ex.goal}`);
    if (ex.approach) blocks.push(`Approach\n${ex.approach}`);
    if (ex.stats) blocks.push(`Results\n${ex.stats}`);
    if (ex.credits) blocks.push(`Credits\n${ex.credits}`);
    return blocks.join('\n\n');
  };

  if (!loading && films.length === 0) return null;

  return (
    <section className="relative bg-black text-white py-16 lg:py-20" data-testid="flyt-films-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <p className="font-italic text-yellow-300 text-xs sm:text-sm tracking-[0.22em] uppercase mb-2 inline-flex items-center gap-1.5">
              <Film className="w-3.5 h-3.5" /> Featured Films
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.95]">
              On the <span className="gold-gradient">slate</span>
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full bg-white/5 ring-1 ring-white/15 hover:bg-yellow-300 hover:text-black transition inline-flex items-center justify-center"
              aria-label="Scroll left"
              data-testid="flyt-films-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full bg-white/5 ring-1 ring-white/15 hover:bg-yellow-300 hover:text-black transition inline-flex items-center justify-center"
              aria-label="Scroll right"
              data-testid="flyt-films-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
          style={{ scrollPaddingLeft: '0.5rem' }}
        >
          {(loading ? Array(4).fill(null) : films).map((s, i) => (
            <button
              key={s?.id || i}
              type="button"
              onClick={() => s && setActiveFilm(s)}
              disabled={!s}
              className="snap-start flex-shrink-0 w-[78%] sm:w-[46%] md:w-[36%] lg:w-[28%] group text-left relative overflow-hidden rounded-2xl bg-[#0a0a0a] ring-1 ring-white/10 hover:ring-yellow-300/60 transition disabled:opacity-50 disabled:cursor-default"
              data-testid={`flyt-film-card-${i}`}
            >
              <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-black">
                {s?.poster && (
                  <img
                    src={resolveImageUrl(s.poster, 400)}
                    alt={s.title || 'Network film'}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                {s && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-300/95 text-black text-[10px] font-bold uppercase tracking-wider shadow-lg" data-testid={`flyt-film-card-${i}-badge`}>
                    <Film className="w-3 h-3" /> Case Study
                  </span>
                )}
                <span className="absolute top-3 right-3 w-10 h-10 rounded-full bg-yellow-300/95 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Play className="w-4 h-4 fill-black ml-0.5" />
                </span>
              </div>
              <div className="p-4">
                {s?.category && (
                  <p className="font-italic text-[10px] uppercase tracking-widest text-yellow-300 mb-1 truncate">
                    {s.category}
                  </p>
                )}
                <p className="font-display text-base sm:text-lg uppercase leading-tight line-clamp-2 text-white">
                  {s?.title || 'Coming soon'}
                </p>
                {s?.client_name && (
                  <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">
                    Case study · {s.client_name}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <NetworkVideoModal
        video={activeFilm ? {
          title: activeFilm.title,
          subtitle: activeFilm.client_name ? `Case Study · ${activeFilm.client_name}` : activeFilm.category,
          video_url: activeFilm.video_url,
          poster: activeFilm.poster,
          body: buildBody(activeFilm),
        } : null}
        athlete={null}
        variant="wide"
        onClose={() => setActiveFilm(null)}
      />
    </section>
  );
};

export default FlytFilmsCarousel;
